import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { 
  Ticket, ArrowLeft, Plus, Download, Trash2, Search, 
  Loader2, CheckCircle, XCircle, Clock, Eye, Ban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminVouchersPage = () => {
  const { token } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Create batch dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBatch, setNewBatch] = useState({
    batch_name: '',
    voucher_type: 'cards',
    value: '',
    quantity: '',
    source: 'giveaway',
    stackable: false,
    min_cards_required: '',
    dealer_only: false,
    exclude_dealers: false,
    expires_in_months: '12'
  });
  
  // View batch dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/vouchers/batches`, { headers });
      setBatches(response.data);
    } catch (err) {
      setError('Failed to load discount code batches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBatch = async () => {
    if (!newBatch.batch_name || !newBatch.value || !newBatch.quantity) {
      alert('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        batch_name: newBatch.batch_name,
        voucher_type: newBatch.voucher_type,
        value: parseFloat(newBatch.value),
        quantity: parseInt(newBatch.quantity),
        source: newBatch.source,
        stackable: newBatch.stackable,
        min_cards_required: newBatch.min_cards_required ? parseInt(newBatch.min_cards_required) : null,
        dealer_only: newBatch.dealer_only,
        exclude_dealers: newBatch.exclude_dealers,
        expires_in_months: newBatch.expires_in_months && newBatch.expires_in_months !== 'none' ? parseInt(newBatch.expires_in_months) : null
      };
      
      await axios.post(`${API}/api/admin/vouchers/batches`, payload, { headers });
      setCreateDialogOpen(false);
      setNewBatch({
        batch_name: '',
        voucher_type: 'cards',
        value: '',
        quantity: '',
        source: 'giveaway',
        stackable: false,
        min_cards_required: '',
        dealer_only: false,
        exclude_dealers: false,
        expires_in_months: '12'
      });
      fetchBatches();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create batch');
    } finally {
      setCreating(false);
    }
  };

  const handleViewBatch = async (batch) => {
    setSelectedBatch(batch);
    setViewDialogOpen(true);
    setLoadingDetails(true);
    
    try {
      const response = await axios.get(`${API}/api/admin/vouchers/batches/${batch.id}`, { headers });
      setBatchDetails(response.data);
    } catch (err) {
      alert('Failed to load batch details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleExport = async (batchId, format = 'codes_only') => {
    try {
      const response = await axios.get(
        `${API}/api/admin/vouchers/batches/${batchId}/export?format=${format}`, 
        { headers }
      );
      
      if (format === 'codes_only') {
        // Download as CSV with PROMO_CODE header
        const csvContent = [
          'PROMO_CODE',
          ...response.data.codes
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${response.data.batch_name.replace(/\s+/g, '_')}_codes.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // Download as CSV
        const headers = ['PROMO_CODE', 'Status', 'Value', 'Expires', 'Redeemed At'];
        const csvContent = [
          headers.join(','),
          ...response.data.rows.map(row => 
            [row.code, row.status, row.value, row.expires_at, row.redeemed_at].join(',')
          )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${response.data.batch_name.replace(/\s+/g, '_')}_full.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert('Failed to export codes');
    }
  };

  const handleVoidAll = async (batchId) => {
    if (!window.confirm('Are you sure you want to void all remaining active codes in this batch?')) {
      return;
    }
    
    try {
      await axios.post(`${API}/api/admin/vouchers/batches/${batchId}/void-all`, {}, { headers });
      fetchBatches();
      if (batchDetails) {
        handleViewBatch(selectedBatch);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to void codes');
    }
  };

  const handleVoidSingle = async (voucherId) => {
    try {
      await axios.post(`${API}/api/admin/vouchers/${voucherId}/void`, {}, { headers });
      if (selectedBatch) {
        handleViewBatch(selectedBatch);
      }
      fetchBatches();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to void code');
    }
  };

  const openDeleteDialog = (batch) => {
    setBatchToDelete(batch);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteBatch = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/admin/vouchers/batches/${batchToDelete.id}`, { headers });
      setDeleteDialogOpen(false);
      setBatchToDelete(null);
      fetchBatches();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete batch');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'giveaway':
        return <Badge className="bg-purple-100 text-purple-800">Giveaway</Badge>;
      case 'sale':
        return <Badge className="bg-green-100 text-green-800">Sale</Badge>;
      case 'promotional':
        return <Badge className="bg-blue-100 text-blue-800">Promotional</Badge>;
      default:
        return <Badge variant="outline">{source}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'redeemed':
        return <Badge className="bg-blue-100 text-blue-800">Redeemed</Badge>;
      case 'voided':
        return <Badge className="bg-gray-100 text-gray-800">Voided</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/admin" 
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Admin
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Ticket className="h-6 w-6 text-[#003594]" />
                Discount Code Management
              </h1>
              <p className="text-gray-500 mt-1">Create and manage discount code batches</p>
            </div>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-[#003594] hover:bg-[#002554]"
              data-testid="create-batch-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Batch
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-900">
                {batches.reduce((sum, b) => sum + b.codes_generated, 0)}
              </div>
              <p className="text-sm text-gray-500">Total Codes Created</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {batches.reduce((sum, b) => sum + b.codes_active, 0)}
              </div>
              <p className="text-sm text-gray-500">Active Codes</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {batches.reduce((sum, b) => sum + b.codes_redeemed, 0)}
              </div>
              <p className="text-sm text-gray-500">Redeemed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-400">
                {batches.reduce((sum, b) => sum + b.codes_voided + b.codes_expired, 0)}
              </div>
              <p className="text-sm text-gray-500">Voided/Expired</p>
            </CardContent>
          </Card>
        </div>

        {/* Batches List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center text-red-500">{error}</CardContent>
          </Card>
        ) : batches.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              No discount code batches created yet. Click &quot;Create Batch&quot; to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {batches.map((batch) => (
              <Card key={batch.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{batch.batch_name}</h3>
                        {getSourceBadge(batch.source)}
                        <Badge variant="outline">
                          {batch.voucher_type === 'cards' 
                            ? `${batch.value} FREE cards` 
                            : `$${batch.value} off`}
                        </Badge>
                        {batch.stackable && (
                          <Badge className="bg-amber-100 text-amber-800">Stackable</Badge>
                        )}
                        {batch.dealer_only && (
                          <Badge className="bg-indigo-100 text-indigo-800">Dealer Only</Badge>
                        )}
                        {batch.exclude_dealers && (
                          <Badge className="bg-orange-100 text-orange-800">Non-Dealer Only</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-6 text-sm text-gray-500 mb-3">
                        <span>Created: {formatDate(batch.created_at)}</span>
                        <span>Expires: {formatDate(batch.expires_at)}</span>
                        {batch.min_cards_required && (
                          <span>Min: {batch.min_cards_required} cards</span>
                        )}
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-md">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ 
                              width: `${(batch.codes_redeemed / batch.codes_generated) * 100}%` 
                            }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">
                          {batch.codes_redeemed} / {batch.codes_generated} redeemed
                        </span>
                      </div>
                      
                      <div className="flex gap-4 mt-2 text-xs">
                        <span className="text-green-600">{batch.codes_active} active</span>
                        <span className="text-blue-600">{batch.codes_redeemed} redeemed</span>
                        <span className="text-gray-400">{batch.codes_voided} voided</span>
                        <span className="text-red-400">{batch.codes_expired} expired</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewBatch(batch)}
                        data-testid={`view-batch-${batch.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleExport(batch.id, 'codes_only')}
                        data-testid={`export-batch-${batch.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Export
                      </Button>
                      {batch.codes_redeemed === 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => openDeleteDialog(batch)}
                          data-testid={`delete-batch-${batch.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Batch Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Discount Code Batch</DialogTitle>
            <DialogDescription>
              Generate a batch of discount codes with shared rules.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="batch_name">Batch Name *</Label>
              <Input
                id="batch_name"
                value={newBatch.batch_name}
                onChange={(e) => setNewBatch({...newBatch, batch_name: e.target.value})}
                placeholder="e.g., Holiday Giveaway 2026"
                data-testid="batch-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type *</Label>
                <Select 
                  value={newBatch.voucher_type} 
                  onValueChange={(v) => setNewBatch({...newBatch, voucher_type: v})}
                >
                  <SelectTrigger data-testid="voucher-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cards">Card-based (Free cards)</SelectItem>
                    <SelectItem value="dollars">Dollar-based ($ off)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="value">
                  {newBatch.voucher_type === 'cards' ? 'Free Cards *' : 'Dollar Amount *'}
                </Label>
                <Input
                  id="value"
                  type="number"
                  min="1"
                  value={newBatch.value}
                  onChange={(e) => setNewBatch({...newBatch, value: e.target.value})}
                  placeholder={newBatch.voucher_type === 'cards' ? 'e.g., 5' : 'e.g., 25.00'}
                  data-testid="value-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quantity">Number of Codes *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  max="10000"
                  value={newBatch.quantity}
                  onChange={(e) => setNewBatch({...newBatch, quantity: e.target.value})}
                  placeholder="e.g., 100"
                  data-testid="quantity-input"
                />
              </div>
              <div>
                <Label>Source</Label>
                <Select 
                  value={newBatch.source} 
                  onValueChange={(v) => setNewBatch({...newBatch, source: v})}
                >
                  <SelectTrigger data-testid="source-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="giveaway">Giveaway</SelectItem>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="promotional">Promotional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Expiration</Label>
              <Select 
                value={newBatch.expires_in_months} 
                onValueChange={(v) => setNewBatch({...newBatch, expires_in_months: v})}
              >
                <SelectTrigger data-testid="expiration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Expiration</SelectItem>
                  <SelectItem value="12">12 Months</SelectItem>
                  <SelectItem value="24">24 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Rules Section */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-sm">Rules & Restrictions</h4>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="stackable"
                  checked={newBatch.stackable}
                  onChange={(e) => setNewBatch({...newBatch, stackable: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="stackable" className="text-sm font-normal cursor-pointer">
                  Allow multiple codes per submission
                </Label>
              </div>
              
              <div>
                <Label htmlFor="min_cards" className="text-sm">Minimum cards required</Label>
                <Input
                  id="min_cards"
                  type="number"
                  min="1"
                  value={newBatch.min_cards_required}
                  onChange={(e) => setNewBatch({...newBatch, min_cards_required: e.target.value})}
                  placeholder="Leave blank for no minimum"
                  className="mt-1"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="dealer_only"
                  checked={newBatch.dealer_only}
                  onChange={(e) => setNewBatch({
                    ...newBatch, 
                    dealer_only: e.target.checked,
                    exclude_dealers: e.target.checked ? false : newBatch.exclude_dealers
                  })}
                  className="rounded"
                />
                <Label htmlFor="dealer_only" className="text-sm font-normal cursor-pointer">
                  Dealer-only code
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="exclude_dealers"
                  checked={newBatch.exclude_dealers}
                  onChange={(e) => setNewBatch({
                    ...newBatch, 
                    exclude_dealers: e.target.checked,
                    dealer_only: e.target.checked ? false : newBatch.dealer_only
                  })}
                  className="rounded"
                />
                <Label htmlFor="exclude_dealers" className="text-sm font-normal cursor-pointer">
                  Exclude dealers (non-dealer only)
                </Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBatch} 
              disabled={creating}
              className="bg-[#003594] hover:bg-[#002554]"
              data-testid="generate-vouchers-btn"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Generate Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Batch Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedBatch?.batch_name}</DialogTitle>
            <DialogDescription>
              View and manage discount codes in this batch.
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : batchDetails && (
            <div className="py-4">
              <div className="flex justify-between items-center mb-4">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleExport(batchDetails.id, 'codes_only')}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export Codes
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleExport(batchDetails.id, 'full')}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export Full CSV
                  </Button>
                </div>
                {batchDetails.vouchers.some(v => v.status === 'active') && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleVoidAll(batchDetails.id)}
                  >
                    <Ban className="h-4 w-4 mr-1" />
                    Void All Remaining
                  </Button>
                )}
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Code</th>
                      <th className="px-4 py-2 text-left font-medium">Status</th>
                      <th className="px-4 py-2 text-left font-medium">Redeemed By</th>
                      <th className="px-4 py-2 text-left font-medium">Redeemed At</th>
                      <th className="px-4 py-2 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {batchDetails.vouchers.map((voucher) => (
                      <tr key={voucher.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono">{voucher.code}</td>
                        <td className="px-4 py-2">{getStatusBadge(voucher.status)}</td>
                        <td className="px-4 py-2">{voucher.redeemed_by_email || '-'}</td>
                        <td className="px-4 py-2">
                          {voucher.redeemed_at ? formatDate(voucher.redeemed_at) : '-'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {voucher.status === 'active' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleVoidSingle(voucher.id)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Batch
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the batch &quot;{batchToDelete?.batch_name}&quot; and all its voucher codes.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm:
            </Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="font-mono"
              data-testid="delete-batch-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteBatch} 
              disabled={deleting || deleteConfirmText !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-batch-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVouchersPage;
